/**
 * 🎯 ONBOARDING TRACKER - Track user onboarding across all roles
 * Consolidates driver onboarding, host setup, and renter activation
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Assignment,
  CheckCircle,
  Schedule,
  Warning,
  Person,
  Home,
  DirectionsCar,
  TrendingUp,
  Refresh,
} from '@mui/icons-material';
import { onboardingService, type OnboardingFunnelData } from '@/services/onboardingService';
import toast from 'react-hot-toast';

export const OnboardingTracker: React.FC = () => {
  const [funnelData, setFunnelData] = useState<OnboardingFunnelData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadFunnelData();
  }, []);

  const loadFunnelData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await onboardingService.getOnboardingFunnel();

      if (response.success && response.data) {
        setFunnelData(response.data);
      } else {
        setError(response.error || 'Failed to load onboarding data');
        // Still set the fallback data from response
        if (response.data) {
          setFunnelData(response.data);
        }
      }
    } catch (err) {
      console.error('Error loading onboarding funnel:', err);
      setError('Failed to load onboarding data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    await loadFunnelData();
    toast.success('Onboarding data refreshed');
  };

  if (isLoading && !funnelData) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Refresh */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Onboarding Funnel Analytics
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
          disabled={isLoading}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>API Connection Issue:</strong> {error}. Showing fallback data below.
          </Typography>
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Onboarding Tracker:</strong> Monitor user progression through onboarding stages
          across all roles. Data from the last 90 days. Different roles have different requirements and completion paths.
        </Typography>
      </Alert>

      {/* Combined Onboarding Overview */}
      {funnelData && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Overall Onboarding Funnel
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              {funnelData.period || 'All time'}
            </Typography>

            {/* Show driver stages as overall funnel */}
            {funnelData.driver.stages.map((stage, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">
                    {stage.name}
                  </Typography>
                  <Chip
                    label={`${stage.count.toLocaleString()} users (${stage.percentage}%)`}
                    color={stage.percentage > 80 ? 'success' : stage.percentage > 60 ? 'warning' : 'error'}
                    size="small"
                  />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stage.percentage}
                  sx={{ height: 8, borderRadius: 4 }}
                  color={stage.percentage > 80 ? 'success' : stage.percentage > 60 ? 'warning' : 'error'}
                />
              </Box>
            ))}
            {funnelData.driver.averageCompletion !== undefined && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Average completion: <strong>{Math.round(funnelData.driver.averageCompletion)}%</strong>
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {funnelData && (
        <Grid container spacing={3}>
          {/* Renter Onboarding */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Person sx={{ fontSize: 32, color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6">
                    Renter Onboarding
                  </Typography>
                </Box>
                <List dense>
                  {funnelData.renter.stages.slice(0, 3).map((stage, index) => {
                    const icon = stage.percentage >= 80 ? <CheckCircle sx={{ color: 'success.main' }} /> :
                                 stage.percentage >= 50 ? <Schedule sx={{ color: 'warning.main' }} /> :
                                 <Warning sx={{ color: 'error.main' }} />;
                    return (
                      <ListItem key={index}>
                        <ListItemIcon>{icon}</ListItemIcon>
                        <ListItemText
                          primary={stage.name}
                          secondary={`${stage.count.toLocaleString()} users (${stage.percentage}%)`}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Host Onboarding */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Home sx={{ fontSize: 32, color: 'secondary.main', mr: 1 }} />
                  <Typography variant="h6">
                    Host Onboarding
                  </Typography>
                </Box>
                <List dense>
                  {funnelData.host.stages.slice(0, 3).map((stage, index) => {
                    const icon = stage.percentage >= 80 ? <CheckCircle sx={{ color: 'success.main' }} /> :
                                 stage.percentage >= 50 ? <Schedule sx={{ color: 'warning.main' }} /> :
                                 <Warning sx={{ color: 'error.main' }} />;
                    return (
                      <ListItem key={index}>
                        <ListItemIcon>{icon}</ListItemIcon>
                        <ListItemText
                          primary={stage.name}
                          secondary={`${stage.count.toLocaleString()} users (${stage.percentage}%)`}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Driver Onboarding */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <DirectionsCar sx={{ fontSize: 32, color: 'success.main', mr: 1 }} />
                  <Typography variant="h6">
                    Driver Onboarding
                  </Typography>
                </Box>
                <List dense>
                  {funnelData.driver.stages.slice(0, 3).map((stage, index) => {
                    const icon = stage.percentage >= 80 ? <CheckCircle sx={{ color: 'success.main' }} /> :
                                 stage.percentage >= 50 ? <Schedule sx={{ color: 'warning.main' }} /> :
                                 <Warning sx={{ color: 'error.main' }} />;
                    return (
                      <ListItem key={index}>
                        <ListItemIcon>{icon}</ListItemIcon>
                        <ListItemText
                          primary={stage.name}
                          secondary={`${stage.count.toLocaleString()} users (${stage.percentage}%)`}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Quick Actions */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Onboarding Actions
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => toast('Contact feature coming soon')}
            >
              Contact Stuck Users
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => toast('Reminder feature coming soon')}
            >
              Send Reminders
            </Button>
            <Button
              variant="outlined"
              onClick={() => toast('Export feature coming soon')}
            >
              Export Funnel Report
            </Button>
            <Button
              variant="outlined"
              startIcon={<TrendingUp />}
              onClick={() => toast('Analytics view coming soon')}
            >
              View Analytics
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Alert severity="success" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>✅ Live Data:</strong> This page now displays real onboarding funnel data from the backend API
          ({funnelData?.period || 'Last 90 days'}). It replaces the scattered follow-up systems with a unified tracker.
        </Typography>
      </Alert>
    </Box>
  );
};